import React from 'react';
import { Button } from '@/ui-components/ui/button';
import { Card, CardContent } from '@/ui-components/ui/card';
import { Badge } from '@/ui-components/ui/badge';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MoreHorizontal,
  Edit,
  Trash2,
  Download,
  Eye
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/util-components/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui-components/ui/tooltip";
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';
import { useDataView } from './data-view';
import { Check } from 'lucide-react';
import { useIsMobile } from '@/lib/hooks/use-mobile';

// --- Skeleton Components ---

export const DataViewGridCardSkeleton = () => {
  return (
    <Card className="border-gray-100 overflow-hidden h-full flex flex-col shadow-sm">
      <CardContent className="p-3 flex flex-col flex-1">
        <div className="relative aspect-square bg-gray-100/50 rounded-lg mb-3 animate-pulse border border-gray-100/50" />
        <div className="flex flex-col flex-1 px-0.5">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse" />
          <div className="h-3 bg-gray-100 rounded w-1/2 mb-4 animate-pulse" />
          <div className="mt-auto space-y-2">
            <div className="h-3 bg-gray-100 rounded w-1/4 animate-pulse" />
            <div className="flex gap-1.5 mt-2">
              <div className="h-6 w-16 bg-gray-100 rounded-md animate-pulse" />
              <div className="h-6 w-12 bg-gray-100 rounded-md animate-pulse" />
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const DataViewListCardSkeleton = () => {
  return (
    <div className="py-3 px-4 border-b border-gray-100 last:border-b-0 bg-white">
      <div className="flex items-center gap-4 animate-pulse">
        {/* Compact Preview Skeleton */}
        <div className="w-12 h-12 bg-gray-100 rounded-md flex-shrink-0" />
        
        {/* Content Skeleton */}
        <div className="flex-1 min-w-0 py-1">
          {/* Title */}
          <div className="h-4 bg-gray-200 rounded w-1/3 min-w-[120px] mb-2.5" />
          
          {/* Subtitle/Metadata row */}
          <div className="flex items-center gap-3">
            <div className="h-3 bg-gray-100 rounded w-24" />
            <div className="h-3 bg-gray-100 rounded w-16 hidden sm:block" />
          </div>
        </div>
        
        {/* Right Side Actions/Badges Skeleton */}
        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <div className="h-6 w-16 bg-gray-100 rounded-md" />
        </div>
      </div>
    </div>
  );
};

export interface GenericItem {
  id: string;
  title: string;
  description?: string;
  previewContent: React.ReactNode;
  previewContentList?: React.ReactNode;
  metadata?: {
    category?: string;
    categories?: string[];
    badgeText?: React.ReactNode;
    size?: string;
    footer?: React.ReactNode;
    platforms?: React.ReactNode[];
    bottomLeft?: React.ReactNode;
    status?: React.ReactNode;
    [key: string]: any;
  };
}

export interface GenericItemActions {
  onItemClick: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  onDownload?: (id: string) => void;
  onRename?: (id: string) => void;
  onPreview?: (id: string) => void;
  customActions?: Array<{
    label: string;
    icon: React.ReactNode;
    onClick: (id: string) => void;
    variant?: 'default' | 'destructive';
    badge?: React.ReactNode;
    disabled?: boolean;
    requiredPermission?: Permission | string;
    requiredPlan?: PlanKey;
  }>;
}

export interface GenericCardProps {
  item: GenericItem;
  actions: GenericItemActions;
  className?: string;
}

// --- Components ---

/**
 * Standard DataView Grid Card.
 */
export const DataViewGridCard: React.FC<GenericCardProps> = ({
  item,
  actions,
  className
}) => {
  const { isMultiSelectMode, selectedIds, onToggleSelect } = useDataView();
  const isMobile = useIsMobile();
  const [isPressed, setIsPressed] = React.useState(false);
  const isSelected = selectedIds.includes(item.id);

  const hasActions = !!(
    actions.onEdit || 
    actions.onDelete || 
    actions.onDownload || 
    actions.onRename || 
    actions.onPreview || 
    (actions.customActions && actions.customActions.length > 0)
  );

  const handleClick = (e: React.MouseEvent) => {
    if (isMultiSelectMode) {
      e.preventDefault();
      onToggleSelect(item.id);
    } else if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      onToggleSelect(item.id, true);
    } else {
      actions.onItemClick(item.id);
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: !isMobile && isPressed ? 0.94 : isSelected && !isMobile ? 0.96 : 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className="h-full"
      onPointerDownCapture={(e) => {
        if (isMobile) return;
        const target = e.target as HTMLElement;
        if (target.closest('[data-card-actions-trigger="true"]')) return;
        setIsPressed(true);
      }}
      onPointerUpCapture={() => setIsPressed(false)}
      onPointerCancelCapture={() => setIsPressed(false)}
      onPointerOutCapture={() => setIsPressed(false)}
    >
      <Card 
        className={cn(
          "group cursor-pointer bg-white transition-all duration-300 border rounded-xl overflow-hidden h-full flex flex-col relative",
          isSelected 
            ? "border-blue-600 ring-2 ring-blue-600/50 shadow-md" 
            : cn("border-gray-200", !isMobile && "hover:border-gray-300 hover:shadow-md"),
          className
        )}
        onClick={handleClick}
      >
        {/* Selection Overlay */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-blue-50/20 z-0 pointer-events-none"
            />
          )}
        </AnimatePresence>

        <CardContent className="p-3 flex flex-col flex-1 relative z-10">
          {/* Preview Content */}
          <div className="relative aspect-square bg-gray-50 rounded-lg mb-3 overflow-hidden flex-shrink-0 border border-gray-100/50">
            {item.previewContent}
            
            {/* Bottom Left Badge/Text */}
            {item.metadata?.bottomLeft && (
              <div className="absolute bottom-2 left-2 z-10 flex items-center justify-center">
                {item.metadata.bottomLeft}
              </div>
            )}
            
            <AnimatePresence>
              {isMultiSelectMode ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1, transition: { type: "spring", stiffness: 500, damping: 30 } }}
                  exit={{ opacity: 0, scale: 0.5, transition: { duration: 0 } }}
                  className="absolute top-2.5 right-2.5 z-50"
                >
                  <div 
                className={cn(
                  "h-5 w-5 rounded-[4px] border flex items-center justify-center shadow-sm transition-all duration-200",
                  isSelected 
                    ? "bg-blue-600 border-blue-600 text-white" 
                    : cn("bg-white/90 border-gray-300 text-transparent", !isMobile && "group-hover:border-gray-400")
                )}
              >
                <Check className={cn("h-3.5 w-3.5 transition-opacity duration-200", isSelected ? "opacity-100" : "opacity-0")} strokeWidth={3} />
              </div>
                </motion.div>
              ) : (
                hasActions && (
                  <motion.div 
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1, transition: { duration: 0.15 } }}
                    exit={{ opacity: 0, scale: 0.8, transition: { duration: 0.1 } }}
                    className="absolute top-2 right-2 z-50"
                  >
                  <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button 
                            data-card-actions-trigger="true"
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 rounded-lg border border-gray-200/70 bg-white/95 p-0 text-gray-500 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <CardActionsMenu actions={actions} itemId={item.id} />
                      </DropdownMenu>
                    </div>
                  </motion.div>
                )
              )}
            </AnimatePresence>
          </div>

        {/* Title and Description */}
        <div className="flex flex-col flex-1 px-0.5">
          <div className="space-y-0.5 mb-2">
            <p className="text-sm font-semibold text-gray-900 line-clamp-2 leading-snug" title={item.title}>
              {item.title}
            </p>
            {item.description && (
              <p className="text-[11px] text-gray-500 truncate" title={item.description}>
                {item.description}
              </p>
            )}
          </div>
          
          {/* Metadata Row */}
          {item.metadata && (
            <div className="mt-auto space-y-2">
              {/* Row 1: Categories */}
              {(item.metadata.categories?.length || item.metadata.category) && (
                <div className="flex items-center min-w-0">
                  {(item.metadata.categories?.length || 0) > 1 ? (
                    <TooltipProvider>
                      <Tooltip delayDuration={300}>
                        <TooltipTrigger asChild>
                          <span className={cn("flex items-center min-w-0 cursor-pointer text-gray-400", !isMobile && "hover:text-gray-600")}>
                            <span className="text-xs capitalize truncate">
                              {item.metadata.categories?.[0] || item.metadata.category}
                            </span>
                            <span className="text-xs transition-colors flex-shrink-0 pl-1">
                              +{(item.metadata.categories?.length || 1) - 1}
                            </span>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent className="bg-popover text-popover-foreground border border-border shadow-sm px-3 py-2">
                          <ul className="list-disc list-inside space-y-1">
                            {item.metadata.categories?.map((cat, i) => (
                              <li key={i} className="text-xs">{cat}</li>
                            ))}
                          </ul>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <span className="text-xs text-gray-500 capitalize truncate">
                      {item.metadata.categories?.[0] || item.metadata.category}
                    </span>
                  )}
                </div>
              )}

              {/* Row 2: Badges */}
              {item.metadata.badgeText && (
                <div className="flex items-center">
                  {Array.isArray(item.metadata.badgeText) ? (
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {item.metadata.badgeText.map((badge, i) => (
                        badge && (
                          <Badge key={i} variant="outline" className="text-xs px-2 py-1 h-6 border-gray-200 text-gray-600">
                            {badge}
                          </Badge>
                        )
                      ))}
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-xs px-2 py-1 h-6 border-gray-200 text-gray-600">
                      {item.metadata.badgeText}
                    </Badge>
                  )}
                </div>
              )}

              {/* Row 3: Status */}
              {item.metadata.status && (
                <div className="flex items-center pt-2">
                  {item.metadata.status}
                </div>
              )}

              {/* Row 4: Size (if no categories) */}
              {item.metadata.size && !item.metadata.category && !item.metadata.categories?.length && (
                <div className="flex items-center">
                  <span className="text-xs text-gray-500 truncate">
                    {item.metadata.size}
                  </span>
                </div>
              )}

              {/* Row 3: Footer/Platforms */}
              {(item.metadata.footer || (item.metadata.platforms && item.metadata.platforms.length > 0)) && (
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center">
                    {item.metadata.footer}
                  </div>
                  {item.metadata.platforms && item.metadata.platforms.length > 0 && (
                    <div className="flex items-center gap-1.5 ml-auto">
                      {item.metadata.platforms.length > 2 ? (
                        <>
                          {item.metadata.platforms[0]}
                          <TooltipProvider>
                            <Tooltip delayDuration={300}>
                              <TooltipTrigger asChild>
                                <span className="flex items-center justify-center w-6 h-6 rounded-md bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600 cursor-pointer hover:bg-gray-100 select-none">
                                  +{item.metadata.platforms.length - 1}
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-popover text-popover-foreground border border-border shadow-sm p-1.5">
                                <div className="flex items-center gap-1.5">
                                  {item.metadata.platforms.map((p, i) => (
                                    <React.Fragment key={i}>{p}</React.Fragment>
                                  ))}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </>
                      ) : (
                        item.metadata.platforms.map((p, i) => (
                          <React.Fragment key={i}>{p}</React.Fragment>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
    </motion.div>
  );
};

/**
 * Standard DataView List Card.
 */
export const DataViewListCard: React.FC<GenericCardProps> = ({
  item,
  actions,
  className
}) => {
  const { isMultiSelectMode, selectedIds, onToggleSelect } = useDataView();
  const isMobile = useIsMobile();
  const [isPressed, setIsPressed] = React.useState(false);
  const isSelected = selectedIds.includes(item.id);

  const hasActions = !!(
    actions.onEdit || 
    actions.onDelete || 
    actions.onDownload || 
    actions.onRename || 
    actions.onPreview || 
    (actions.customActions && actions.customActions.length > 0)
  );

  const handleClick = (e: React.MouseEvent) => {
    if (isMultiSelectMode) {
      e.preventDefault();
      onToggleSelect(item.id);
    } else if (e.metaKey || e.ctrlKey) {
      e.preventDefault();
      onToggleSelect(item.id, true);
    } else {
      actions.onItemClick(item.id);
    }
  };

  return (
    <motion.div 
      layout
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0, scale: !isMobile && isPressed ? 0.99 : 1 }}
      transition={{ type: "spring", stiffness: 400, damping: 30 }}
      className={cn(
        "group cursor-pointer py-3 px-4 border-b border-gray-100 last:border-b-0 transition-colors duration-200",
        isSelected
          ? cn("bg-blue-50/60", !isMobile && "hover:bg-blue-50/80")
          : cn("bg-white", !isMobile && "hover:bg-gray-50"),
        className
      )}
      onClick={handleClick}
      onPointerDownCapture={(e) => {
        if (isMobile) return;
        const target = e.target as HTMLElement;
        if (target.closest('[data-card-actions-trigger="true"]')) return;
        setIsPressed(true);
      }}
      onPointerUpCapture={() => setIsPressed(false)}
      onPointerCancelCapture={() => setIsPressed(false)}
      onPointerOutCapture={() => setIsPressed(false)}
    >
      <div className="flex items-center gap-4">
        {/* Compact Preview */}
        <div className="w-12 h-12 bg-gray-50 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0 relative">
          {item.previewContentList || item.previewContent}
          {item.metadata?.bottomLeft && (
            <div className="absolute bottom-0.5 left-0.5 z-10 flex items-center justify-center scale-75 origin-bottom-left">
              {item.metadata.bottomLeft}
            </div>
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3">
            <p className="text-sm font-semibold text-gray-900 line-clamp-1 sm:line-clamp-2">
              {item.title}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 sm:mt-1">
            {item.description && (
              <p className="text-xs text-gray-500 truncate max-w-[120px] sm:max-w-[200px] md:max-w-none">
                {item.description}
              </p>
            )}
            {item.metadata?.category && (
              <p className="text-xs text-gray-500 capitalize">
                {item.metadata.category}
              </p>
            )}
            {item.metadata?.size && (
              <p className="text-xs text-gray-500">
                {item.metadata.size}
              </p>
            )}
            {item.metadata?.footer && (
              <div className="flex-shrink-0 hidden sm:block">
                {item.metadata.footer}
              </div>
            )}
          </div>
        </div>

        {/* Right Side Info: Status, Badge & Platforms */}
        {(item.metadata?.badgeText || item.metadata?.status || (item.metadata?.platforms && item.metadata.platforms.length > 0)) && (
          <div className="hidden md:flex items-center gap-3 flex-shrink-0">
            {item.metadata?.status && (
              <div className="flex-shrink-0">
                {item.metadata.status}
              </div>
            )}
            
            {item.metadata?.platforms && item.metadata.platforms.length > 0 && (
              <div className="flex items-center gap-1.5">
                {item.metadata.platforms.map((p, i) => (
                  <React.Fragment key={i}>{p}</React.Fragment>
                ))}
              </div>
            )}
            
            {item.metadata?.badgeText && (
              Array.isArray(item.metadata.badgeText) ? (
                <div className="flex items-center gap-1.5">
                  {item.metadata.badgeText.map((badge, i) => (
                    badge && (
                      <Badge key={i} variant="outline" className="text-xs px-2 py-1 h-6 border-gray-200 text-gray-600">
                        {badge}
                      </Badge>
                    )
                  ))}
                </div>
              ) : (
                <Badge variant="outline" className="text-xs px-2 py-1 h-6 border-gray-200 text-gray-600">
                  {item.metadata.badgeText}
                </Badge>
              )
            )}
          </div>
        )}

        <AnimatePresence>
          {isMultiSelectMode ? (
            <motion.div 
              initial={{ opacity: 0, scale: 0.5, width: 0, marginLeft: 0 }}
              animate={{ opacity: 1, scale: 1, width: 20, marginLeft: 12, transition: { type: "spring", stiffness: 500, damping: 30 } }}
              exit={{ opacity: 0, scale: 0.5, width: 0, marginLeft: 0, transition: { duration: 0 } }}
              className="flex-shrink-0"
            >
              <div 
                className={cn(
                  "h-5 w-5 rounded-[4px] border flex items-center justify-center shadow-sm transition-all duration-200",
                  isSelected 
                    ? "bg-blue-600 border-blue-600 text-white" 
                    : cn("bg-white border-gray-300 text-transparent", !isMobile && "group-hover:border-gray-400")
                )}
              >
                <Check className={cn("h-3.5 w-3.5 transition-opacity duration-200", isSelected ? "opacity-100" : "opacity-0")} strokeWidth={3} />
              </div>
            </motion.div>
          ) : (
            hasActions && (
              <motion.div 
                initial={{ opacity: 0, width: 0, marginLeft: 0 }}
                animate={{ opacity: 1, width: 32, marginLeft: 12, transition: { type: "spring", stiffness: 500, damping: 30 } }}
                exit={{ opacity: 0, width: 0, marginLeft: 0, transition: { duration: 0.1 } }}
              >
                <div className="opacity-100 md:opacity-0 md:group-hover:opacity-100">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button 
                        data-card-actions-trigger="true"
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 rounded-lg border border-gray-200/70 bg-white/95 p-0 text-gray-500 shadow-sm transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <CardActionsMenu actions={actions} itemId={item.id} />
                  </DropdownMenu>
                </div>
              </motion.div>
            )
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Internal Helper
const CardActionsMenu = ({ actions, itemId }: { actions: GenericItemActions, itemId: string }) => {
  return (
    <DropdownMenuContent align="end" className="w-48">
      {actions.onPreview && (
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation();
            actions.onPreview!(itemId);
          }}
          className="cursor-pointer"
        >
          <Eye className="h-4 w-4 mr-2" />
          Vorschau
        </DropdownMenuItem>
      )}
      {actions.onEdit && (
        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation();
              actions.onEdit!(itemId);
            }}
            className="cursor-pointer"
          >
            <Edit className="h-4 w-4 mr-2" />
            Bearbeiten
          </DropdownMenuItem>
        </PermissionFeatureWrapper>
      )}
      {actions.onDownload && (
        <DropdownMenuItem 
          onClick={(e) => {
            e.stopPropagation();
            actions.onDownload!(itemId);
          }}
          className="cursor-pointer"
        >
          <Download className="h-4 w-4 mr-2" />
          Download
        </DropdownMenuItem>
      )}
      {actions.onRename && (
        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation();
              actions.onRename!(itemId);
            }}
            className="cursor-pointer"
          >
            <Edit className="h-4 w-4 mr-2" />
            Umbenennen
          </DropdownMenuItem>
        </PermissionFeatureWrapper>
      )}
      {actions.customActions?.map((action, index) => {
        const item = (
          <DropdownMenuItem 
            key={index}
            disabled={action.disabled}
            onClick={(e) => {
              if (action.disabled) return;
              e.stopPropagation();
              action.onClick(itemId);
            }}
            className={cn(
              "cursor-pointer",
              action.variant === 'destructive' && "text-red-600 focus:text-red-600 focus:bg-red-50",
              action.disabled && "opacity-50 cursor-not-allowed pointer-events-none"
            )}
          >
            <div className="h-4 w-4 mr-2">
              {action.icon}
            </div>
            {action.label}
            {action.badge && (
              <span className="ml-auto">{action.badge}</span>
            )}
          </DropdownMenuItem>
        );

        if (action.requiredPermission && action.requiredPlan) {
          return (
            <PermissionFeatureWrapper 
              key={index}
              requiredPermission={action.requiredPermission} 
              requiredPlan={action.requiredPlan}
            >
              {item}
            </PermissionFeatureWrapper>
          );
        }

        return item;
      })}
      {actions.onDelete && (
        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
          <DropdownMenuItem 
            onClick={(e) => {
              e.stopPropagation();
              actions.onDelete!(itemId);
            }}
            className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Löschen
          </DropdownMenuItem>
        </PermissionFeatureWrapper>
      )}
    </DropdownMenuContent>
  );
};
